import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import { calculateAggregate, isAggregationConfigured } from '@h20-shared/dashboards';
import type { AggregateCell, AggregateValue } from '@h20-shared/dashboards';
import { GRID_MAIN_VALUE_KEY } from './dashboard-grid.config';
import type { GridMainValueConfig } from './dashboard-grid.schema';

/** The rows that share one value of the group by column. */
type RowGroup = {
    /** The cells every row in the group holds the same value for */
    readonly sharedCells: RowObject;
    readonly rows: RowObject[];
};

/** Whether the main value divides one measure by another instead of showing a single number. */
export function isRatioMainValue(mainValue: GridMainValueConfig): boolean {
    return mainValue.denominator.enabled && isAggregationConfigured(mainValue.denominator);
}

/** The rows a view shows: one per `groupByColumn` value, with the main value added. */
export function buildViewRows(
    rows: readonly RowObject[],
    groupByColumn: string | undefined,
    mainValue: GridMainValueConfig,
    summedColumns: readonly string[]
): RowObject[] {
    // A `Map` keeps groups in the order they first appear, so rows do not jump between renders.
    const groups = new Map<AggregateCell | null, RowGroup>();
    for (const [index, row] of rows.entries()) {
        // Without a column, the index puts each row in its own group.
        const groupKey = groupByColumn === undefined ? index : (row[groupByColumn] ?? null);
        const group = groups.get(groupKey);
        if (group === undefined) {
            groups.set(groupKey, { sharedCells: { ...row }, rows: [row] });
            continue;
        }
        group.rows.push(row);
        for (const key of Object.keys(group.sharedCells)) {
            if (row[key] !== group.sharedCells[key]) {
                delete group.sharedCells[key];
            }
        }
    }

    const ratio = isRatioMainValue(mainValue);
    const columnsToSum = summedColumns.filter((column) => column !== groupByColumn);

    return [...groups.values()].map((group) => {
        const summedCells: RowObject = {};
        for (const column of columnsToSum) {
            const total = calculateAggregate(group.rows, { column, aggregate: 'sum' });
            if (typeof total === 'number') {
                summedCells[column] = total;
            }
        }
        const numerator = calculateAggregate(group.rows, mainValue.numerator);
        return {
            ...group.sharedCells,
            ...summedCells,
            [GRID_MAIN_VALUE_KEY]: ratio
                ? ratioOf(numerator, calculateAggregate(group.rows, mainValue.denominator))
                : numerator,
        };
    });
}

/** One measure over another, or nothing when either is not a number or would divide by zero. */
function ratioOf(numerator: AggregateValue, denominator: AggregateValue): number | undefined {
    return typeof numerator === 'number' && typeof denominator === 'number' && denominator !== 0
        ? numerator / denominator
        : undefined;
}
